'use client'

/**
 * StockScrollDock — a small live chart that follows you down the feed. Once the main chart scrolls out
 * of view it fades in, docked bottom-right, so you never lose the price while reading news/alerts below
 * (owner 2026-07-15). Price + %-change ride as a bubble on top of a compact sparkline. The header is a
 * DRAG handle (move it anywhere), it can MINIMIZE to a slim price chip, CLOSE, or jump BACK to the full
 * chart. Collapses to a slim price chip on phones so it never covers content. Polls like the live pulse.
 */

import { useCallback, useEffect, useRef, useState } from 'react'
import { X, ArrowUp, Minus, ChevronUp, Bell } from 'lucide-react'
import { whenVisible } from '@/lib/whenVisible'
import { applyMinichart, minichartSince, minichartUrl, type MinichartState } from '@/lib/minichartMerge'
import TickerLogo from '@/components/TickerLogo'

type Bar = { t: number; c: number }
const fmtPx = (n: number) => (n < 1 ? n.toFixed(3) : n.toFixed(2))

// Offset the "back to chart" scroll to clear the sticky header, matching the anchor's own scroll-mt-28
// (112px) so it lands exactly where the section-nav "Chart" tab does. The old scrollIntoView scrolled
// the nearest scroll ancestor in increments and never settled on the chart — that was the reported bug.
const HEADER_OFFSET = 112

export default function StockScrollDock({
  symbol, initialPrice, initialPrevClose, anchorId = 'feed-chart-anchor', showAlertCta = false,
}: { symbol: string; initialPrice: number | null; initialPrevClose: number | null; anchorId?: string; showAlertCta?: boolean }) {
  const [visible, setVisible] = useState(false)
  const [dismissed, setDismissed] = useState(false)
  const [minimized, setMinimized] = useState(false)
  const [price, setPrice] = useState<number | null>(initialPrice)
  const [pct, setPct] = useState<number | null>(null)
  const [prevClose, setPrevClose] = useState<number | null>(initialPrevClose)
  const [bars, setBars] = useState<Bar[]>([])
  // Free-drag position (top-left px, fixed). null = default docked bottom-right.
  const [pos, setPos] = useState<{ x: number; y: number } | null>(null)
  const dockRef = useRef<HTMLDivElement>(null)
  const drag = useRef<{ dx: number; dy: number } | null>(null)

  // Show the dock once the main chart has scrolled ABOVE the viewport; hide it while the chart is in view.
  useEffect(() => {
    const el = document.getElementById(anchorId)
    if (!el) return
    const io = new IntersectionObserver(
      ([e]) => setVisible(!e.isIntersecting && e.boundingClientRect.top < 0),
      { rootMargin: '0px', threshold: 0 },
    )
    io.observe(el)
    return () => io.disconnect()
  }, [anchorId])

  // Incremental after the first full load (src/lib/minichartMerge.ts); keyed by symbol like StockLivePulse.
  const mcRef = useRef<{ symbol: string; state?: MinichartState<Bar> }>({ symbol })
  const pull = useCallback(async () => {
    if (dismissed) return
    if (mcRef.current.symbol !== symbol) mcRef.current = { symbol }
    const now = Date.now()
    const since = minichartSince(mcRef.current.state, now)
    const [q, mc] = await Promise.all([
      fetch(`/api/watchlist/quotes?symbols=${encodeURIComponent(symbol)}`, { cache: 'no-store' }).then((r) => (r.ok ? r.json() : null)).catch(() => null),
      fetch(minichartUrl(symbol, since), { cache: 'no-store' }).then((r) => (r.ok ? r.json() : null)).catch(() => null),
    ])
    if (mcRef.current.symbol !== symbol) return
    const it = Array.isArray(q?.quotes) ? q.quotes[0] : null
    if (it) {
      if (it.price != null) setPrice(Number(it.price))
      if (it.chgPct != null) setPct(Number(it.chgPct))
      if (it.prevClose != null) setPrevClose(Number(it.prevClose))
    }
    const slim = Array.isArray(mc?.bars) ? { ...mc, bars: mc.bars.map((b: { t: number; c: number }) => ({ t: b.t, c: Number(b.c) })) } : mc
    const next = applyMinichart<Bar>(mcRef.current.state, slim, since, now)
    if (next && next !== mcRef.current.state) { mcRef.current.state = next; setBars(next.bars) }
    if (mc?.prevClose != null) setPrevClose(Number(mc.prevClose))
  }, [symbol, dismissed])

  // Only poll while the dock is actually on screen — no work when the main chart is visible.
  useEffect(() => {
    if (!visible || dismissed) return
    pull()
    const id = setInterval(whenVisible(pull), 15000)
    return () => clearInterval(id)
  }, [visible, dismissed, pull])

  // ── Drag (pointer events on the header). Ignores drags that start on a button. Clamps to the viewport.
  const onDragStart = (e: React.PointerEvent) => {
    if ((e.target as HTMLElement).closest('button')) return
    const box = dockRef.current?.getBoundingClientRect()
    if (!box) return
    drag.current = { dx: e.clientX - box.left, dy: e.clientY - box.top }
    try { (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId) } catch { /* older browsers */ }
  }
  const onDragMove = (e: React.PointerEvent) => {
    if (!drag.current) return
    const box = dockRef.current?.getBoundingClientRect()
    const w = box?.width ?? 248, h = box?.height ?? 130
    const x = Math.max(4, Math.min(e.clientX - drag.current.dx, window.innerWidth - w - 4))
    const y = Math.max(4, Math.min(e.clientY - drag.current.dy, window.innerHeight - h - 4))
    setPos({ x, y })
  }
  const onDragEnd = (e: React.PointerEvent) => {
    drag.current = null
    try { (e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId) } catch { /* noop */ }
  }

  if (dismissed || !visible || price == null) return null

  const up = (pct ?? 0) >= 0
  const stroke = up ? '#34d399' : '#fb7185'

  // Compact sparkline: index-spaced polyline of closes, plus a dashed prev-close baseline.
  const W = 232, H = 56, PAD = 4
  let path = '', baseY: number | null = null
  if (bars.length >= 2) {
    const cs = bars.map((b) => b.c)
    let lo = Math.min(...cs), hi = Math.max(...cs)
    if (prevClose != null) { lo = Math.min(lo, prevClose); hi = Math.max(hi, prevClose) }
    const span = hi - lo || 1
    const x = (i: number) => PAD + (i / (bars.length - 1)) * (W - PAD * 2)
    const y = (v: number) => PAD + (1 - (v - lo) / span) * (H - PAD * 2)
    path = cs.map((c, i) => `${i ? 'L' : 'M'}${x(i).toFixed(1)},${y(c).toFixed(1)}`).join(' ')
    if (prevClose != null) baseY = y(prevClose)
  }

  const jumpUp = () => {
    const el = document.getElementById(anchorId)
    if (el) {
      const y = el.getBoundingClientRect().top + window.scrollY - HEADER_OFFSET
      window.scrollTo({ top: Math.max(0, y), behavior: 'smooth' })
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' })
    }
  }

  const pctChip = pct != null && (
    <span className={`rounded-md px-1.5 py-0.5 text-[11px] font-bold tabular-nums ${up ? 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-400' : 'bg-rose-500/15 text-rose-600 dark:text-rose-400'}`}>
      {up ? '+' : ''}{pct.toFixed(2)}%
    </span>
  )

  return (
    <div
      ref={dockRef}
      className="fixed z-40 animate-[fadeInUp_.25s_ease]"
      style={pos ? { left: pos.x, top: pos.y } : { right: 16, bottom: 16 }}
    >
      <div className="w-[168px] overflow-hidden rounded-2xl border border-border bg-background/95 shadow-2xl dark:shadow-black/60 ring-1 ring-border dark:ring-white/5 backdrop-blur sm:w-[248px]">
        {/* header = DRAG handle: logo + symbol (+ price/% when minimized) + minimize + close */}
        <div
          onPointerDown={onDragStart}
          onPointerMove={onDragMove}
          onPointerUp={onDragEnd}
          onPointerCancel={onDragEnd}
          className="flex touch-none select-none items-center gap-2 px-2.5 py-1.5 cursor-grab active:cursor-grabbing"
        >
          <TickerLogo symbol={symbol} size={20} rounded="rounded" />
          <span className="text-[12px] font-black tracking-tight text-amber-600 dark:text-amber-400">${symbol}</span>
          {minimized && (
            <span className="flex items-baseline gap-1">
              <span className="text-[13px] font-extrabold tabular-nums text-foreground">${fmtPx(price)}</span>
              {pctChip}
            </span>
          )}
          <div className="ml-auto flex items-center gap-0.5">
            <button onClick={() => setMinimized((m) => !m)} className="-mr-0.5 rounded p-0.5 text-muted-foreground transition-colors hover:bg-accent dark:hover:bg-white/5 hover:text-foreground" aria-label={minimized ? 'Expand mini chart' : 'Minimize mini chart'}>
              {minimized ? <ChevronUp className="h-3.5 w-3.5" /> : <Minus className="h-3.5 w-3.5" />}
            </button>
            <button onClick={() => setDismissed(true)} className="-mr-0.5 rounded p-0.5 text-muted-foreground transition-colors hover:bg-accent dark:hover:bg-white/5 hover:text-foreground" aria-label="Close mini chart">
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>

        {!minimized && (
          <>
            <div className="flex items-baseline gap-1.5 px-2.5 pb-1">
              <span className="text-[15px] font-extrabold tabular-nums text-foreground">${fmtPx(price)}</span>
              {pctChip}
            </div>
            {/* sparkline → back to full chart */}
            <button onClick={jumpUp} className="group block w-full px-1 pb-1" title="Back to full chart">
              <svg viewBox={`0 0 ${W} ${H}`} className="h-9 w-full sm:h-14" preserveAspectRatio="none">
                {baseY != null && <line x1={PAD} x2={W - PAD} y1={baseY} y2={baseY} stroke="#52525b" strokeWidth={1} strokeDasharray="3 3" />}
                {path && (
                  <>
                    <path d={`${path} L${W - PAD},${H - PAD} L${PAD},${H - PAD} Z`} fill={stroke} fillOpacity={0.1} stroke="none" />
                    <path d={path} fill="none" stroke={stroke} strokeWidth={1.6} strokeLinejoin="round" strokeLinecap="round" />
                  </>
                )}
              </svg>
              <span className="flex items-center justify-center gap-1 py-0.5 text-[10px] font-semibold text-muted-foreground transition-colors group-hover:text-amber-600 dark:group-hover:text-amber-400">
                <ArrowUp className="h-2.5 w-2.5" /> Back to chart
              </span>
            </button>
            {/* Convert affordance that rides WITH the price: a bouncing visitor who scrolled past the
                chart still has one clear way to stay. Anon lands on the gated /alerts (signup); members
                land on their live dashboard. */}
            {showAlertCta && (
              <a href="/alerts" className="mx-2.5 mb-2 mt-0.5 flex items-center justify-center gap-1.5 rounded-lg bg-gradient-to-b from-amber-300 to-amber-500 py-1.5 text-[12px] font-bold text-black transition-colors hover:from-amber-200 hover:to-amber-400">
                <Bell className="h-3 w-3" /> Get alerts
              </a>
            )}
          </>
        )}
      </div>
      <style>{`@keyframes fadeInUp{from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:translateY(0)}}`}</style>
    </div>
  )
}
